import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import Draggable from 'react-draggable';

let instanceCounter = 0;

export const useDraggableModal = (zIndex, onFocus, options = {}) => {
    const initialWidth = options.initialWidth || 800;
    const initialHeight = options.initialHeight || null;
    const isVisible = !!(options.visible || options.open || options.show || options.isVisible);
    const modalKey = useMemo(() => options.modalKey || `modal-id-${Math.random().toString(36).substr(2, 9)}`, [options.modalKey]);
    const disabled = !!options.disabled;
    const handleClass = `modal-drag-handle-${modalKey}`;
    
    const draggleRef = useRef(null);
    const [size, setSize] = useState({ width: initialWidth, height: initialHeight });
    const [effectiveZ, setEffectiveZ] = useState(zIndex || 10000);
    const isResizing = useRef(false);

    // Controle de cascata
    const offsetRef = useRef(null);
    if (offsetRef.current === null) {
        instanceCounter++;
        const step = instanceCounter % 8;
        offsetRef.current = { top: step * 30, left: step * 30 };
    }

    const zIndexRef = useRef(zIndex);
    useEffect(() => {
        zIndexRef.current = zIndex;
    }, [zIndex]);

    const lastAppliedZ = useRef(0);

    // Função de Foco Soberana
    const handleFocus = useCallback((skipNotify = false) => {
        if (disabled) return;
        const applyZ = () => {
            try {
                if (draggleRef.current) {
                    const currentGlobal = window.__DRAGGABLE_TOP_Z || 10000;
                    const propZ = zIndexRef.current;
                    
                    let topZValue;
                    if (propZ && propZ > currentGlobal) {
                        topZValue = propZ;
                        window.__DRAGGABLE_TOP_Z = propZ;
                    } else {
                        topZValue = currentGlobal + 1;
                        window.__DRAGGABLE_TOP_Z = topZValue;
                    }

                    // Se já aplicamos este z-index, não fazemos nada
                    if (lastAppliedZ.current === topZValue) return;
                    lastAppliedZ.current = topZValue;

                    const topZ = String(topZValue);

                    // 1. Aplica via Inline Style (Prioridade 1)
                    draggleRef.current.style.setProperty('z-index', topZ, 'important');
                    draggleRef.current.style.setProperty('pointer-events', 'auto', 'important');
                    setEffectiveZ(topZValue);

                    // 2. Injeta/Atualiza Style Tag Dinâmica (Prioridade 2 - Global Authority)
                    const styleId = `style-force-z-${modalKey}`;
                    let styleEl = document.getElementById(styleId);
                    if (!styleEl) {
                        styleEl = document.createElement('style');
                        styleEl.id = styleId;
                        document.head.appendChild(styleEl);
                    }
                    
                    const wrapClass = `draggable-modal-wrap-${modalKey}`;
                    styleEl.innerHTML = `
                        .${wrapClass}, 
                        .${wrapClass} .ant-modal-wrap,
                        .${wrapClass} .ant-modal-mask,
                        .modal-container-${modalKey} {
                            z-index: ${topZ} !important;
                            pointer-events: auto !important;
                        }
                        .${wrapClass} .ant-modal-wrap {
                            pointer-events: none !important;
                        }
                        .modal-container-${modalKey} {
                            pointer-events: auto !important;
                        }
                    `;

                    // Notifica o React para atualizar o estado de longo prazo (apenas se não for skip)
                    if (onFocus && !skipNotify) {
                        onFocus();
                    }
                }
            } catch (err) {
                console.warn("Erro ao forçar foco soberano:", err);
            }
        };

        applyZ();
    }, [onFocus, modalKey]);

    // Sincroniza quando o zIndex via prop muda, mas evita loop
    useEffect(() => {
        if (isVisible && zIndex && zIndex !== lastAppliedZ.current) {
            handleFocus(true);
        }
    }, [zIndex, isVisible, handleFocus]);

    // Auto-foco ao abrir
    useEffect(() => {
        if (isVisible) {
            handleFocus();
            
            const timer = setTimeout(() => handleFocus(true), 50);

            // Estilos base para garantir comportamento de janelas
            const baseStyleId = 'draggable-modal-base-styles';
            if (!document.getElementById(baseStyleId)) {
                const style = document.createElement('style');
                style.id = baseStyleId;
                style.innerHTML = `
                    .ant-modal-root { z-index: 10000; }
                    .ant-modal-wrap { pointer-events: none !important; }
                    .ant-modal { pointer-events: none !important; }
                    .ant-modal-content { pointer-events: auto !important; }
                    .ant-modal-close { pointer-events: auto !important; z-index: 100000 !important; }
                    .ant-modal-header { cursor: move !important; pointer-events: auto !important; }
                `;
                document.head.appendChild(style);
            }

            return () => {
                clearTimeout(timer);
                const specificStyle = document.getElementById(`style-force-z-${modalKey}`);
                if (specificStyle) specificStyle.remove();
            };
        }
    }, [isVisible, handleFocus, modalKey]);

    // Listener nativo em modo de captura
    useEffect(() => {
        const el = draggleRef.current;
        if (!el || !isVisible) return;

        const nativeListener = (e) => {
            handleFocus();
        };

        el.addEventListener('mousedown', nativeListener, true);
        return () => el.removeEventListener('mousedown', nativeListener, true);
    }, [handleFocus, isVisible]);

    const handleResizeStart = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        handleFocus();
        isResizing.current = true;
        
        const startX = e.clientX;
        const startY = e.clientY;
        const startWidth = draggleRef.current.offsetWidth;
        const startHeight = draggleRef.current.offsetHeight;

        const onMouseMove = (moveEvent) => {
            if (!isResizing.current) return;
            const newWidth = Math.max(400, startWidth + (moveEvent.clientX - startX));
            const newHeight = Math.max(200, startHeight + (moveEvent.clientY - startY));
            setSize({ width: newWidth, height: newHeight });
        };

        const onMouseUp = () => {
            isResizing.current = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [handleFocus]);


    const draggableProps = {
        modalRender: (modal) => (
            <Draggable
                nodeRef={draggleRef}
                handle={`.${handleClass}, .ant-modal-header`}
                onStart={handleFocus}
                onDrag={handleFocus} // Garante foco constante durante o arraste
            >
                <div
                    ref={draggleRef}
                    className={`modal-container-${modalKey}`}
                    style={{ 
                        pointerEvents: 'auto',
                        position: 'absolute',
                        top: 60 + offsetRef.current.top,
                        left: 100 + offsetRef.current.left,
                        width: size.width,
                        height: size.height || 'auto',
                        maxHeight: '92vh',
                        display: isVisible ? 'flex' : 'none',
                        flexDirection: 'column',
                        zIndex: effectiveZ,
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                        overflow: options.overflow || 'hidden',
                        border: '1px solid #e5e7eb'
                    }}
                >
                    <div style={{ height: '100%', position: 'relative', display: 'flex', flexDirection: 'column', width: '100%' }}>
                        {modal}
                    </div>

                    <div 
                        onMouseDown={handleResizeStart}
                        style={{ 
                            position: 'absolute', 
                            right: 0, 
                            bottom: 0, 
                            width: 24, 
                            height: 24, 
                            cursor: 'nwse-resize', 
                            zIndex: 9999,
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-end',
                            padding: '4px 8px',
                        }}
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 11L1 1M11 6L6 11M11 1L1 11" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </div>
                </div>
            </Draggable>
        ),
        wrapClassName: `draggable-modal-wrap-${modalKey}`,
        mask: false,
        styles: {
            mask: { display: 'none' },
            wrapper: { pointerEvents: 'none' }
        },
        style: {
            padding: 0,
            margin: 0,
            pointerEvents: 'none',
        },
        wrapProps: {
            style: { 
                pointerEvents: 'none',
                display: isVisible ? '' : 'none'
            },
            onMouseDownCapture: handleFocus
        },
    };

    return draggableProps;
};
